// ModalShell — local overlay shell shared by RoleFormModal +
// PermissionMatrix. Mirrors the FormDialog overlay chrome without
// the field-registry constraint that prevents text/textarea/number
// rendering. Lives under components/role/ rather than components/
// because the v2 ListPage + DetailPage primitives don't need a
// generic overlay primitive yet — extracting prematurely would
// duplicate FormDialog without merging benefit.

import type { ReactNode } from "react";

export function ModalShell({
  title,
  onClose,
  size = "md",
  children,
}: {
  title: string;
  onClose: () => void;
  size?: "md" | "lg";
  children: ReactNode;
}) {
  const width = size === "lg" ? 760 : 520;
  return (
    <div
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: "fixed",
        inset: 0,
        // composition-lint-exempt: modal/overlay backdrop scrim — pending shared --color-scrim token (DRIFT-776)
        background: "rgba(15, 23, 42, 0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 50,
        padding: 16,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        style={{
          background: "var(--color-surface)",
          borderRadius: "var(--radius-lg, 12px)",
          boxShadow: "var(--shadow-overlay, 0 25px 50px -12px rgba(0,0,0,0.25))",
          width: "100%",
          maxWidth: width,
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <header
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "16px 20px 12px",
            borderBottom: "1px solid var(--color-border-subtle)",
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: 18,
              fontWeight: 500,
              color: "var(--color-text)",
            }}
          >
            {title}
          </h2>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              color: "var(--color-text-muted)",
              fontSize: 22,
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </header>
        {children}
      </div>
    </div>
  );
}
